import type {
  CompanyConfigProfileOutput,
  CompanyOptionsProfile,
  CompanySettingsProfile,
  PayGroupOptionsProfile,
  PayGroupSettingsProfile,
} from "../../generated/core-api";
import {
  CS_BOOLEAN_PARENT,
  fieldsForGroup,
  GROUP_LABELS,
  GROUPS,
  enabledSections,
  type OptionFieldDef,
} from "../types";
import { assertNever } from "../utils/assertNever";
import s from "./ProfileSummary.module.css";

interface ProfileSummaryProps {
  profile: CompanyConfigProfileOutput;
  /** Spacious two-column treatment used by the bulk-create redesign Review step. */
  presentation?: "compact" | "review";
}

/**
 * Read-only rendering of a {@link CompanyConfigProfileOutput}.
 *
 * Provider-first: one top-level block per group ("Datapay Reasons" /
 * "Direct Access Reasons"), each nesting both Company options and Pay Group
 * options as sub-headers (no tabs — all groups visible). Fields are sorted A→Z
 * (en-NZ) within each group and use verbatim Christmas UI labels. The Pay Group
 * sub-section is silently omitted when `profile.PayGroupOptions` is absent.
 *
 * Used to view the content of a specific (e.g. historical) config version.
 */
export function ProfileSummary({
  profile,
  presentation = "review",
}: ProfileSummaryProps) {
  const sections = enabledSections();

  return (
    <div
      className={`${s.panel} ${
        presentation === "review" ? s.reviewPanel : ""
      }`}
    >
      {GROUPS.map((group) => (
        <div key={group} className={s.section}>
          <h4 className={s.heading}>
            {presentation === "review"
              ? GROUP_LABELS[group].replace("Datapay", "DataPay")
              : GROUP_LABELS[group]}
          </h4>
          {sections.map((section) => {
            // Gracefully skip a sub-section when the corresponding profile key is absent
            // (older configs may not carry CompanyOptions or PayGroupOptions).
            // Exception: if the section declares a `secondaryProfileKey` and that key is
            // present, the section is shown even when the primary key is absent.
            // This handles "Pay Group options" when PayGroupSettings is present but
            // PayGroupOptions is not (e.g. only EnableLIW was set on a config).
            // Fields bound to the absent primary sub-profile default gracefully to
            // Off / "—" via optional-chaining at the field level.
            if (
              !profile[section.profileKey] &&
              !(section.secondaryProfileKey && profile[section.secondaryProfileKey])
            ) {
              return null;
            }

            const fields = fieldsForGroup(section, group);
            if (fields.length === 0) return null;

            return (
              <div key={section.id} className={s.sectionGroup}>
                <h5 className={s.groupHeading}>{section.title}</h5>
                <dl className={s.grid}>
                  {fields.map((field) =>
                    renderFieldRow(
                      field,
                      profile.CompanyOptions ?? {},
                      profile.PayGroupOptions,
                      profile.CompanySettings,
                      profile.PayGroupSettings,
                    )
                  )}
                </dl>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Field row renderer (read-only) ─────────────────────────────────────────

function renderFieldRow(
  field: OptionFieldDef,
  companyValues: CompanyOptionsProfile,
  pgValues: PayGroupOptionsProfile | null | undefined,
  csValues: CompanySettingsProfile | null | undefined,
  pgsValues: PayGroupSettingsProfile | null | undefined,
) {
  switch (field.kind) {
    case "boolean": {
      const on = companyValues[field.key] ?? false;
      return (
        <div key={field.key} className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={`${s.state} ${on ? s.on : s.off}`}>{on ? "On" : "Off"}</dd>
        </div>
      );
    }

    case "escalation": {
      const escalationOn = companyValues.EnableLeaveRequestEscalation ?? false;
      const escalationDays = companyValues.LeaveRequestEscalation;
      const stateText = escalationOn
        ? typeof escalationDays === "number"
          ? `On · after ${escalationDays} day(s)`
          : "On"
        : "Off";
      return (
        <div key="escalation" className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={`${s.state} ${escalationOn ? s.on : s.off}`}>{stateText}</dd>
        </div>
      );
    }

    case "pg-boolean": {
      const on = pgValues?.[field.key] ?? false;
      return (
        <div key={field.key} className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={`${s.state} ${on ? s.on : s.off}`}>{on ? "On" : "Off"}</dd>
        </div>
      );
    }

    case "pg-number": {
      // Only show conditional sub-controls when their parent boolean is on.
      if (field.parentKey !== undefined) {
        const parentOn = pgValues?.[field.parentKey] ?? false;
        if (!parentOn) return null;
      }
      const val = pgValues?.[field.key];
      return (
        <div key={field.key} className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={s.value}>{val != null ? String(val) : "—"}</dd>
        </div>
      );
    }

    case "cs-boolean": {
      // Reads from CompanySettings sub-profile; gracefully defaults to false when
      // the sub-profile is absent (legacy configs that pre-date #886).
      //
      // CO-DA-14 → CO-DA-01 coupling gate (shared with the edit form via
      // CS_BOOLEAN_PARENT): a dependent shows Off whenever its parent is off,
      // regardless of the stored value, so the read-only summary matches what the
      // edit form displays and what cleanProfile() persists.
      const parentKey = CS_BOOLEAN_PARENT[field.key];
      const parentOn =
        parentKey !== undefined ? (csValues?.[parentKey] ?? false) : true;
      const on = parentOn ? (csValues?.[field.key] ?? false) : false;
      return (
        <div key={field.key} className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={`${s.state} ${on ? s.on : s.off}`}>{on ? "On" : "Off"}</dd>
        </div>
      );
    }

    case "cs-string": {
      // Renders the URL as plain text, or an em-dash when absent/null.
      // Gracefully handles legacy configs where CompanySettings is absent.
      const url = csValues?.[field.key];
      return (
        <div key={field.key} className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={s.value}>{url != null && url !== "" ? url : "—"}</dd>
        </div>
      );
    }

    case "pg-settings-boolean": {
      // Reads from PayGroupSettings sub-profile; gracefully defaults to false when
      // the sub-profile is absent (legacy configs that pre-date #886).
      const on = pgsValues?.[field.key] ?? false;
      return (
        <div key={field.key} className={s.row}>
          <dt className={s.label}>{field.label}</dt>
          <dd className={`${s.state} ${on ? s.on : s.off}`}>{on ? "On" : "Off"}</dd>
        </div>
      );
    }

    default:
      return assertNever(field);
  }
}
